import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Wrench,
    BookOpen,
    Newspaper,
    Tag,
    Star,
    HelpCircle,
    Inbox,
    Loader2,
    ArrowUpRight,
    AlertCircle,
} from "lucide-react";

import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const cards = [
    { key: "services", label: "Services", icon: Wrench, to: "/admin/services" },
    { key: "case_studies", label: "Case Studies", icon: BookOpen, to: "/admin/case-studies" },
    { key: "blog_posts", label: "Blog Posts", icon: Newspaper, to: "/admin/blog" },
    { key: "pricing_packages", label: "Pricing Packages", icon: Tag, to: "/admin/pricing" },
    { key: "testimonials", label: "Testimonials", icon: Star, to: "/admin/testimonials" },
    { key: "faqs", label: "FAQs", icon: HelpCircle, to: "/admin/faqs" },
    { key: "enquiries", label: "Total Enquiries", icon: Inbox, to: "/admin/enquiries" },
    { key: "new_enquiries", label: "New Enquiries", icon: AlertCircle, to: "/admin/enquiries", highlight: true },
];

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard/summary");
                setData(data);
            } catch (err) {
                toast.error(formatError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading)
        return (
            <div className="grid place-items-center py-20 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );

    const counts = data?.counts || {};
    const recent = data?.recent_enquiries || [];

    return (
        <div className="space-y-10" data-testid="dashboard-page">
            <header>
                <div className="label-eyebrow">Overview</div>
                <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-slate-950 mt-1">
                    Welcome back
                </h1>
                <p className="text-sm text-slate-500 mt-2">
                    A quick snapshot of your content and recent enquiries.
                </p>
            </header>

            {/* Stats grid */}
            <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {cards.map((c) => {
                    const Icon = c.icon;
                    const value = counts[c.key] ?? 0;
                    return (
                        <Link
                            key={c.key}
                            to={c.to}
                            className={`group bg-white border border-slate-200 rounded-md p-5 transition-all hover:-translate-y-[1px] hover:shadow-sm hover:border-slate-300 ${
                                c.highlight && value > 0 ? "ring-1 ring-slate-950" : ""
                            }`}
                            data-testid={`stat-${c.key}`}
                        >
                            <div className="flex items-start justify-between">
                                <Icon className="w-5 h-5 text-slate-500 group-hover:text-slate-950 transition-colors" />
                                <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-950 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                            </div>
                            <div className="mt-6">
                                <div className="font-display text-3xl font-black tracking-tight text-slate-950">
                                    {value}
                                </div>
                                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 font-bold mt-1">
                                    {c.label}
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </section>

            {/* Recent enquiries */}
            <section className="bg-white border border-slate-200 rounded-md">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                    <div>
                        <h2 className="font-display text-lg font-bold text-slate-950">
                            Recent Enquiries
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Latest leads from the public website
                        </p>
                    </div>
                    <Link
                        to="/admin/enquiries"
                        className="text-xs font-semibold text-slate-700 hover:text-slate-950 inline-flex items-center gap-1"
                    >
                        View all <ArrowUpRight className="w-3 h-3" />
                    </Link>
                </div>
                {recent.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center mx-auto">
                            <Inbox className="w-5 h-5 text-slate-400" />
                        </div>
                        <p className="text-sm text-slate-500 mt-3">
                            No enquiries yet. Once visitors submit your contact form, they
                            will land here.
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {recent.map((e) => (
                            <li
                                key={e.id}
                                className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
                            >
                                <div className="min-w-0">
                                    <div className="font-semibold text-slate-950 text-sm truncate">
                                        {e.full_name}{" "}
                                        <span className="text-slate-400 font-normal">
                                            · {e.business_name || "—"}
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-500 truncate mt-0.5">
                                        {e.email} · {e.service_needed || "General"}
                                    </div>
                                </div>
                                <Badge
                                    className={
                                        e.status === "new"
                                            ? "bg-slate-950 text-white"
                                            : e.status === "contacted"
                                              ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                                              : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                                    }
                                >
                                    {e.status}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
